import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminService } from '../../services/adminService';
import { BadgeCheck, CheckCircle2, XCircle, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  paid: 'bg-blue-100 text-blue-700',
};

const PayoutRequests = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [itemsByRequest, setItemsByRequest] = useState({});
  const [loadingItems, setLoadingItems] = useState({});
  const [updating, setUpdating] = useState({});

  const { data: requests = [], isLoading, refetch } = useQuery({
    queryKey: ['adminPayoutRequests', statusFilter],
    queryFn: () => AdminService.getPayoutRequests(statusFilter === 'all' ? null : statusFilter),
  });

  const totalAmount = useMemo(
    () => requests.reduce((sum, r) => sum + Number(r.payout_amount || 0), 0),
    [requests]
  );

  const toggleExpand = async (requestId) => {
    if (expanded === requestId) {
      setExpanded(null);
      return;
    }
    setExpanded(requestId);
    if (!itemsByRequest[requestId]) {
      setLoadingItems((p) => ({ ...p, [requestId]: true }));
      const items = await AdminService.getPayoutRequestItems(requestId);
      setItemsByRequest((p) => ({ ...p, [requestId]: items }));
      setLoadingItems((p) => ({ ...p, [requestId]: false }));
    }
  };

  const updateStatus = async (req, status) => {
    const note = window.prompt('Optional note (leave empty to skip):', '');
    setUpdating((p) => ({ ...p, [req.request_id]: true }));
    try {
      const res = await AdminService.updatePayoutRequest(req.request_id, status, note || null);
      if (!res.success) {
        alert(res.error || 'Failed to update payout request');
        return;
      }
      await refetch();
    } finally {
      setUpdating((p) => ({ ...p, [req.request_id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payout requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payout Requests</h1>
          <p className="text-gray-600 mt-1">Review telecaller payout requests</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total Requests</p>
          <p className="text-2xl font-bold text-gray-900">{requests.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total Payout Amount</p>
          <p className="text-2xl font-bold text-gray-900">₹{totalAmount.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Status Filter</p>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telecaller</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payout</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.map((r) => {
                const statusClass = statusColors[r.status] || 'bg-gray-100 text-gray-700';
                return (
                  <React.Fragment key={r.request_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{r.telecaller_name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{r.telecaller_email || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded ${statusClass}`}>
                          {r.status}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {r.requested_at ? new Date(r.requested_at).toLocaleString() : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{r.total_orders}</td>
                      <td className="px-6 py-4 whitespace-nowrap">₹{Number(r.total_amount || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        ₹{Number(r.payout_amount || 0).toFixed(2)}
                        <div className="text-xs text-gray-500">{r.commission_percent}%</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => toggleExpand(r.request_id)}
                            className="px-2 py-1 text-gray-600 hover:text-gray-900"
                          >
                            {expanded === r.request_id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </button>
                          <button
                            onClick={() => updateStatus(r, 'approved')}
                            disabled={updating[r.request_id]}
                            className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 inline-flex items-center gap-2"
                          >
                            <CheckCircle2 size={16} /> Approve
                          </button>
                          <button
                            onClick={() => updateStatus(r, 'rejected')}
                            disabled={updating[r.request_id]}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-2"
                          >
                            <XCircle size={16} /> Reject
                          </button>
                          <button
                            onClick={() => updateStatus(r, 'paid')}
                            disabled={updating[r.request_id]}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                          >
                            <CreditCard size={16} /> Paid
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expanded === r.request_id && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 bg-gray-50">
                          {loadingItems[r.request_id] ? (
                            <div className="text-sm text-gray-600">Loading items...</div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {(itemsByRequest[r.request_id] || []).map((it) => (
                                <div key={it.order_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                                  <div className="text-sm font-semibold text-gray-900">{it.customer_name || 'Customer'}</div>
                                  <div className="text-xs text-gray-500">Order: {String(it.order_id || '').slice(-8)}</div>
                                  <div className="text-xs text-gray-500">
                                    Delivered: {it.delivered_at ? new Date(it.delivered_at).toLocaleString() : '—'}
                                  </div>
                                  <div className="text-xs text-gray-500">Amount: ₹{Number(it.final_amount || 0).toFixed(2)}</div>
                                  <div className="text-xs text-green-700 font-semibold">Payout: ₹{Number(it.payout_amount || 0).toFixed(2)}</div>
                                </div>
                              ))}
                              {(itemsByRequest[r.request_id] || []).length === 0 && (
                                <div className="text-sm text-gray-500">No items found.</div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {requests.length === 0 && (
          <div className="text-center py-12">
            <BadgeCheck size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No payout requests found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayoutRequests;

